import hljs from 'highlight.js';

import JotformQuill from '../components';

const TOAST_DURATION = 5000;
let contentLocalStorageKey;
let jotformQuill;

hljs?.configure({ useBR: false });

export const initHighlightJs = () => {
  document.querySelectorAll('pre')?.forEach(el => {
    if (hljs?.highlightBlock) {
      hljs.highlightBlock(el);
    }
  });
};

try {
  initHighlightJs();
} catch (error) {
  console.log(error);
}

const setAriaLabel = (el, label) => {
  return el.setAttribute('aria-label', label);
};

const applyAccessibilityFeatures = editor => {
  const query = editor.parentElement.querySelector('.ql-toolbar');
  if (!query) {
    return;
  }

  const elements = query.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (element.getAttribute('role') === 'button' || element.getAttribute('type') === 'button') {
      if (!element.getAttribute('aria-label')) {
        setAriaLabel(element, element.getAttribute('class'));
      }
    }
  }
};

const handleOnInit = (quill, quillName, callback) => {
  if (quillName === 'replyQuill') {
    const prevContent = window.sessionStorage.getItem(contentLocalStorageKey);
    if (prevContent) {
      // eslint-disable-next-line no-param-reassign
      quill.root.innerHTML = prevContent;
    }
  } else if (quillName === 'annotateQuill') {
    setTimeout(() => {
      const ticketAnnotateValue = document.querySelector('#ticket-annotate')?.innerHTML;
      if (ticketAnnotateValue && ticketAnnotateValue !== '<p><br></p>') {
        // eslint-disable-next-line no-param-reassign
        quill.root.innerHTML = ticketAnnotateValue;
      }
    }, 1000);
  }

  if (callback) {
    callback(quill, contentLocalStorageKey);
  }
};

const handleReplyOnInit = (quillName, callback) => {
  return quill => {
    handleOnInit(quill, quillName, callback);
  };
};

const handleTextChange = (quill, quillName, callback) => {
  if (quillName === 'replyQuill') {
    const quillContent = quill.root.innerHTML;
    window.sessionStorage.setItem(contentLocalStorageKey, quillContent);
  }

  if (callback) {
    callback(quill, contentLocalStorageKey);
  }
};

const handleReplyTextChange = (quillName, callback) => {
  return quill => {
    handleTextChange(quill, quillName, callback);
  };
};

const getCallbackBasedOnPage = (jfQuillEvent, quillName, callback) => {
  if (jfQuillEvent === 'onInit') {
    return handleReplyOnInit(quillName, callback);
  }
  if (jfQuillEvent === 'onTextChanged') {
    return handleReplyTextChange(quillName, callback);
  }
  return null;
};

const createToastContainer = containerElSelector => {
  const containerEl = document.querySelector(containerElSelector);
  const createToastEl = document.createElement('div');
  createToastEl.className = 'jf-toasts';
  createToastEl.id = `jf-toasts-${containerElSelector.replace(/^#/, '')}`;
  containerEl.appendChild(createToastEl);
};

const createToast = (message, fileName, toastType) => {
  const toastEl = document.createElement('div');
  toastEl.className = `jf-toast ${toastType}`;

  if (toastType === 'progress') {
    toastEl.innerHTML = `${message} <span class="jf-progress"></span> <br> ${fileName}`;
  } else {
    toastEl.innerHTML = `${message} <br> ${fileName}`;
  }

  return toastEl;
};

const pushToast = (message, fileName, toastContainer, toastType = 'danger', autoRemove = true) => {
  const toastEl = createToast(message, fileName, toastType);
  toastContainer.appendChild(toastEl);

  if (autoRemove) {
    setTimeout(() => {
      toastEl.parentElement.removeChild(toastEl);
    }, TOAST_DURATION);
  }

  return toastEl;
};

const removeToast = toastEl => {
  toastEl.parentElement.removeChild(toastEl);
};

const handleImageUpload = async (quill, base64URL, contentType, fileName, index) => {
  let cleanFileName = fileName.replace(/[\u00A0-\u9999<>&]/g, i => {
    return `&#${i.charCodeAt(0)};`;
  });

  cleanFileName = cleanFileName.replace(/(?:\.(?![^.]+$)|[^\w.])+/g, '-');

  const toastContainer = document.getElementById(`jf-toasts-${quill.options.editorContainerId.replace(/^#/, '')}`);
  const loadingToast = pushToast('Please wait, uploading...', cleanFileName, toastContainer, 'progress', false);

  const fileSize = parseInt(base64URL.length * 0.66, 10);
  const validationResult = jotformQuill.validateFile(contentType, fileSize);

  if (!validationResult.valid) {
    pushToast(validationResult.readable, cleanFileName, toastContainer);
    removeToast(loadingToast);
    return;
  }

  const formData = new window.FormData();
  formData.append('name', cleanFileName);
  formData.append('image', base64URL);

  await fetch('/opt/quilljs/imageUploader.php', {
    method: 'POST',
    body: formData
  })
    .then(response => response.json())
    .then(data => {
      removeToast(loadingToast);

      if (data.error) {
        pushToast(data.message, cleanFileName, toastContainer);
        return;
      }
      quill.insertEmbed(index, 'image', data.url, 'user');
      quill.setSelection(index + 1, 'user');
    })
    .catch(error => {
      console.log(error);
    });
};

export const initJotformQuill = (quillName, editorId = '#editor', editorContainerId = '#editor-container', callbacks) => {
  let userType = 'NORMAL';

  if (window.userType) {
    userType = window.userType;
  } else if (window.forumUser && window.forumUser.account_type) {
    userType = window.forumUser.account_type;
  }

  const pathArr = window.location.pathname.split('/');
  const indexOfAnswers = pathArr.indexOf('answers');
  const questionIdPart = pathArr[indexOfAnswers + 1];
  const questionID = questionIdPart.split('-')[0];
  contentLocalStorageKey = `contentOfQuestion_${questionID}`;
  const quilljsSettings = {
    maxFileSize: 8e6,
    theme: 'snow',
    modules: {
      syntax: {
        highlight: text => {
          return hljs.highlightAuto(text).value;
        }
      },
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike', {
            color: []
          }, {
            background: []
          }],
          ['link', 'jotformImage'],
          [{
            align: []
          }],
          [{
            list: 'ordered'
          }, {
            list: 'bullet'
          }],
          [{
            indent: '-1'
          }, {
            indent: '+1'
          }],
          ['code-block', 'source-code'],
          ['clean']
        ],
        handlers: {
          // we need to create pseudo event-handlers before the real modules are ready while initializing the custom toolbar buttons to prevent attach warnings
          jotformImage: () => {},
          'source-code': () => {}
        }
      },
      imageDropAndPaste: {
        userType: userType,
        onImageDropOrPaste: handleImageUpload
      },
      imageResize: {
        modules: ['Resize']
      },
      jotformImage: {
        maxFileSize: 8e6,
        onImageAdd: handleImageUpload
      },
      magicUrl: {
        urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)|(mailto:[\S]+)|(tel:[\S]+)/,
        globalRegularExpression: /(https?:\/\/|www\.|mailto:|tel:)[\S]+/g,
        normalizeUrlOptions: {
          stripFragment: false,
          stripWWW: true,
          normalizeProtocol: false
        }
      },
      sourceCode: {
        userType: userType
      },
      magicEmail: {}
    },
    onInit: getCallbackBasedOnPage('onInit', quillName, callbacks?.onInit ?? null), // TODO: change it
    onTextChanged: getCallbackBasedOnPage('onTextChanged', quillName, callbacks?.onTextChanged ?? null) // TODO: change it
  };

  createToastContainer(editorContainerId);

  try {
    jotformQuill = new JotformQuill(editorContainerId, quilljsSettings, editorId);
    window[quillName] = jotformQuill;
    jotformQuill.render();
    applyAccessibilityFeatures(document.querySelector(editorContainerId));
  } catch (err) {
    console.error(err);
  }
};
